import styled from 'styled-components';
import { SummaryContainer } from '../containers';
import COLORS from '../../colors';
import { useAppSelector } from '../../redux/hooks';
import { RootState } from '../../redux/store';
import mapBurstToNumber from '../BudgetSplitSection/mapBurstToNumber';
import { DURATION_SECONDS, MONTH_PASCAL } from '../../constants';
import { useEffect, useState } from 'react';
import { getMonthsInRange } from '../../services/date';
import { mapPascalMonthToLowercaseMonth } from '../../util';

interface ColumnProps {
  isFirst?: boolean;
}

interface StyledItemProps {
  isDiscount?: boolean;
}

const StyledContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  width: 100%;
  margin-bottom: 16px;
  margin-top: 16px;
`;

const StyledHeader = styled.h4`
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 16px;
  color: ${COLORS.WHITE};
`;

const StyledColumns = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  width: 100%;
  margin-bottom: 16px;
  margin-top: 16px;
`;

const Column = styled.div<ColumnProps>`
  flex: 1;
  width: 100%;
  border-right: ${(props) =>
    props.isFirst ? `1px solid ${COLORS.NEUTRAL_700}` : 'none'};
  padding-right: ${(props) => (props.isFirst ? '16px' : '0px')};
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
`;

const StyledTableHeading = styled.p`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: 13.33px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  color: ${COLORS.NEUTRAL_500};
  padding: 0 12px;
`;

const StyledTableHeadingSmall = styled.p`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  font-size: 13.33px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  color: ${COLORS.NEUTRAL_500};
  padding: 0 12px;
  max-width: 60px;
`;

const StyledItem = styled.div<StyledItemProps>`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  color: ${COLORS.NEUTRAL_200};
  padding: 12px;
  background-color: ${(props) =>
    props.isDiscount ? 'transparent' : COLORS.NEUTRAL_925};
  border-radius: 8px;
  max-width: ${(props) => (props.isDiscount ? '60px' : 'auto')};
`;

const CampaignCosts = () => {
  const { campaignDurations, advertiserDiscount } = useAppSelector(
    (state: RootState) => state.detail
  );
  const { startDate, endDate } = useAppSelector(
    (state: RootState) => state.detail.campaignLength
  );
  const calculated = useAppSelector((state: RootState) => state.calculated);
  const [months, setMonths] = useState<string[]>([]);

  useEffect(() => {
    if (!startDate || !endDate) {
      return;
    }

    const monthsInRange = getMonthsInRange(
      new Date(startDate),
      new Date(endDate)
    );
    setMonths(monthsInRange);
  }, [startDate, endDate]);

  return (
    <StyledContainer>
      <SummaryContainer title="Costs">
        <StyledColumns>
          <Column isFirst>
            <StyledHeader>CPT</StyledHeader>
            <Row>
              <StyledTableHeadingSmall></StyledTableHeadingSmall>
              {Object.entries(campaignDurations).map(
                ([burst, isBurstSelected]) => {
                  if (isBurstSelected) {
                    const burstNumber = mapBurstToNumber(
                      burst as DURATION_SECONDS
                    );

                    return (
                      <StyledTableHeading>
                        {burstNumber}" Burst
                      </StyledTableHeading>
                    );
                  }
                }
              )}
            </Row>
            {months.map((month) => {
              if (!month) {
                return;
              }

              const selectedMonth = mapPascalMonthToLowercaseMonth(
                month as MONTH_PASCAL
              );
              return (
                <Row>
                  <StyledTableHeadingSmall>{month}</StyledTableHeadingSmall>
                  <StyledItem>
                    ${calculated[selectedMonth].totalCPT.toFixed(2)}
                  </StyledItem>
                  <StyledItem>
                    ${calculated[selectedMonth].totalCPT.toFixed(2)}
                  </StyledItem>
                </Row>
              );
            })}
          </Column>
          <Column>
            <StyledHeader>CPM</StyledHeader>
            <Row>
              {Object.entries(campaignDurations).map(
                ([burst, isBurstSelected]) => {
                  if (isBurstSelected) {
                    const burstNumber = mapBurstToNumber(
                      burst as DURATION_SECONDS
                    );

                    return (
                      <StyledTableHeading>
                        {burstNumber}" Burst
                      </StyledTableHeading>
                    );
                  }
                }
              )}
              <StyledTableHeadingSmall>Discount</StyledTableHeadingSmall>
            </Row>
            {months.map((month) => {
              if (!month) {
                return;
              }

              const selectedMonth = mapPascalMonthToLowercaseMonth(
                month as MONTH_PASCAL
              );
              return (
                <Row>
                  <StyledTableHeadingSmall>{month}</StyledTableHeadingSmall>
                  <StyledItem>
                    ${calculated[selectedMonth].totalCPM.toFixed(2)}
                  </StyledItem>
                  <StyledItem>
                    ${calculated[selectedMonth].totalCPM.toFixed(2)}
                  </StyledItem>
                  <StyledItem isDiscount>
                    {advertiserDiscount * 100}%
                  </StyledItem>
                </Row>
              );
            })}
          </Column>
        </StyledColumns>
      </SummaryContainer>
    </StyledContainer>
  );
};

export default CampaignCosts;
